import { Grid, Typography, TextField, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextareaAutosize } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { IMaterialConsumeDialogProps } from '../../interfaces';

export const MaterialConsumeDialog: React.SFC<IMaterialConsumeDialogProps> = ({
    materials,
    onConsume,
    ...props
}: IMaterialConsumeDialogProps) => {

    const [open, setOpen] = useState(false);
    const [quantities, setQuantities] = useState<{ [materialId: string]: number }>({});
    const [consumedReason, setConsumedReason] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            const initialQuantities = materials.reduce((acc, material) => {
                acc[material.materialId ?? ""] = 0;
                return acc;
            }, {} as { [materialId: string]: number });
            setQuantities(initialQuantities);
        }
    }, [open, materials]);

    const handleQuantityChange = (materialId: string, maxQuantity: number, value: string) => {
        const quantity = Math.min(Math.max(0, parseInt(value, 10) || 0), maxQuantity);
        setQuantities(prevQuantities => ({
            ...prevQuantities,
            [materialId]: quantity,
        }));
    };

    const isFormValid = () => {
        const anyQuantitiesSet = materials.some(material => quantities[material.materialId ?? ""] > 0);
        return consumedReason.trim() !== '' && anyQuantitiesSet;
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isFormValid()) {
            const materialData = materials.map(material => ({
                materialId: material.materialId ?? "",
                quantity: quantities[material.materialId ?? ""],
            }));
            if (onConsume)
                onConsume(materialData, consumedReason);
            setOpen(false);
        } else {
            setErrorMessage('Please ensure all quantities are set and a reason is provided.');
        }
    };

    const onCancel = () => {
        setOpen(false);
    };

    return (
        <span>
            <Button variant="outlined" color="warning" onClick={() => setOpen(true)}>
                Consume
            </Button>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
            >
                <form
                    onSubmit={onSubmit}
                    noValidate
                >
                    <DialogTitle>
                        Consume Material
                    </DialogTitle>
                    <DialogContent>

                        <Typography mb={2}>Select the quantity to be consumed for each material and provide a reason for why the material is being consumed.</Typography>

                        {materials.map(material => (
                            <Grid container key={material.materialId} spacing={2} alignItems="center" marginBottom="1em">
                                <Grid item xs={6}>
                                    <Typography>{material.l3}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Consumed"
                                        inputProps={{ min: 0, max: material.quantity }}
                                        value={quantities[material.materialId ?? ""]}
                                        onChange={(e) => handleQuantityChange(material.materialId ?? "", material.quantity ?? 0, e.target.value)}
                                        error={quantities[material.materialId ?? ""] > (material.quantity ?? 0)}
                                        helperText={`of ${material.quantity} available`}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                        <TextareaAutosize
                            minRows={2}
                            style={{
                                borderRadius: '5px',
                                borderColor: '#ccc',
                                width: '100%',
                                padding: '.5em'
                            }}
                            placeholder="Consumed Reason"
                            value={consumedReason}
                            onChange={(e) => setConsumedReason(e.target.value)}
                            required
                        />
                        {errorMessage &&
                            <DialogContentText color="error">
                                {errorMessage}
                            </DialogContentText>
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancel} color="warning">Cancel</Button>
                        <Button type="submit" color="primary" disabled={!isFormValid()}>
                            Consume
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </span>
    );
};
